import { DiscordSDK } from '@discord/embedded-app-sdk';
import { API_BASE } from './lib';

declare global {
  interface Window {
    __TKT_DISCORD_CLIENT_ID?: string;
  }
}

export type ActivityIdentity = Readonly<{
  user: { id: string; username: string; avatar: string | null };
  guildId: string;
  voiceChannelId: string | null;
  activityToken: string;
}>;

const VITE_CLIENT_ID = (import.meta.env.VITE_DISCORD_CLIENT_ID as string | undefined) ?? '';

/** Discord injects frame_id into the iframe URL when an activity launches. */
export function isInDiscordActivity(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has('frame_id');
}

async function fetchClientId(): Promise<string> {
  const fromVite = VITE_CLIENT_ID.trim();
  if (fromVite) return fromVite;
  const fromWindow = (window.__TKT_DISCORD_CLIENT_ID || '').trim();
  if (fromWindow) return fromWindow;

  const res = await fetch(`${API_BASE}/activity/config`);
  if (!res.ok) throw new Error(`Activity config fetch failed (${res.status})`);
  const body = (await res.json()) as { clientId: string | null };
  if (!body.clientId) throw new Error('Discord client ID not configured on server');
  return body.clientId;
}

/**
 * Run the full Discord Activity OAuth handshake:
 *   ready → authorize → backend code exchange → authenticate → mint activity token.
 * Resolves with the verified identity + the activity token used for our API.
 */
export async function setupDiscordActivity(): Promise<ActivityIdentity> {
  const clientId = await fetchClientId();
  const sdk = new DiscordSDK(clientId);

  await sdk.ready();

  const { code } = await sdk.commands.authorize({
    client_id: clientId,
    response_type: 'code',
    state: '',
    prompt: 'none',
    scope: ['identify']
  });

  const handshakeRes = await fetch(`${API_BASE}/activity/handshake`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      code,
      guildId: sdk.guildId,
      channelId: sdk.channelId
    })
  });

  if (!handshakeRes.ok) {
    const errBody = await handshakeRes.json().catch(() => ({}));
    throw new Error(errBody.error || `Activity handshake failed (${handshakeRes.status})`);
  }

  const { accessToken, activityToken, user, guildId, voiceChannelId } = (await handshakeRes.json()) as {
    accessToken: string;
    activityToken: string;
    user: ActivityIdentity['user'];
    guildId: string;
    voiceChannelId: string | null;
  };

  await sdk.commands.authenticate({ access_token: accessToken });

  return { user, guildId, voiceChannelId, activityToken };
}
